/**
 * React Query hooks for drift detection data.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getAgentBaseline,
  getAgentDriftEvents,
  getAgentRules,
  updateAgentRule,
  updateDriftEventStatus,
  recomputeBaseline,
} from "@/services/driftDetectionService";
import type { DriftEventStatus, DriftRuleUpdate } from "@/types/drift";
import { useApiReady } from "@/hooks/useApi";

export function useAgentBaseline(agentId: number) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["drift-baseline", agentId],
    queryFn: () => getAgentBaseline(agentId),
    enabled: agentId > 0 && isApiReady,
    retry: false,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

export function useAgentDriftEvents(
  agentId: number,
  filters?: { severity?: string; drift_type?: string }
) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["drift-events", agentId, filters],
    queryFn: () => getAgentDriftEvents(agentId, { ...filters, limit: 50 }),
    enabled: agentId > 0 && isApiReady,
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
  });
}

export function useAgentRules(agentId: number) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["drift-rules", agentId],
    queryFn: () => getAgentRules(agentId),
    enabled: agentId > 0 && isApiReady,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateDriftEventStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ eventId, status }: { eventId: number; status: DriftEventStatus }) =>
      updateDriftEventStatus(eventId, status),
    onSuccess: (_data, variables) => {
      toast.success(`Event ${variables.status.toLowerCase().replace("_", " ")}`);
      queryClient.invalidateQueries({ queryKey: ["drift-events"] });
      queryClient.invalidateQueries({ queryKey: ["drift-baseline"] });
    },
    onError: () => {
      toast.error("Failed to update drift event");
    },
  });
}

export function useUpdateAgentRule(agentId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ruleId, update }: { ruleId: string; update: DriftRuleUpdate }) =>
      updateAgentRule(agentId, ruleId, update),
    onError: () => {
      toast.error("Failed to update rule");
    },
    onSettled: () => {
      // Invalidate on settled (not success) to avoid race condition with optimistic UI
      queryClient.invalidateQueries({ queryKey: ["drift-rules", agentId] });
    },
  });
}

export function useRecomputeBaseline() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (agentId: number) => recomputeBaseline(agentId),
    onSuccess: (data, agentId) => {
      toast.success("Baseline computed");
      queryClient.setQueryData(["drift-baseline", agentId], data);
      queryClient.invalidateQueries({ queryKey: ["drift-events", agentId] });
      queryClient.invalidateQueries({ queryKey: ["drift-rules", agentId] });
    },
    onError: (error: Error) => {
      if (error.message?.includes("404") || error.message?.includes("no trace data")) {
        toast.error("Not enough trace data to compute a baseline yet");
      } else {
        toast.error("Failed to compute baseline");
      }
    },
  });
}
